import { SystemStyleFunction } from "@chakra-ui/theme-tools";
import { hexToRGB } from "utils/colorUtils";

type StyleProps = Parameters<SystemStyleFunction>[0];

const theme = {
  styles: {
    global: {
      body: {
        overflowX: "hidden",
        backgroundColor: "bright",
        fontWeight: "500",
        color: "dark.500",
      },
      "html, body, #root": {
        height: "full",
        fontSize: {
          base: "14px",
          sm: "16px",
        },
      },
      "*:focus": {
        boxShadow: "none !important",
      },
      "*": {
        webkitTapHighlightColor: "rgba(255, 255, 255, 0)",
      },
    },
  },
  fonts: {
    heading: "Montserrat",
    body: "Montserrat",
  },
  colors: {
    primary: {
      400: "#AB434B",
      500: "#AB434B",
      600: "#963B42",
      700: "#963B42",
    },
    secondary: {
      400: "#F2AF4A",
      500: "#F2AF4A",
      600: "#D69A40",
      700: "#D69A40",
    },
    dark: {
      400: "#484250",
      500: "#36313E",
      600: "#2F2A36",
    },
    bright: {
      300: "#FDEEE2",
      400: "#FAE5D4",
      500: "#FBE3D3",
      600: "#EDD6C7",
      700: "#E2CEC2",
    },
  },
  components: {
    Container: {
      baseStyle: {
        maxW: "container.sm",
        px: "4",
      },
    },
    Progress: {
      baseStyle: {
        track: {
          bg: hexToRGB("#F2AF4A", 0.35),
          borderRadius: "full",
        },
      },
    },
    Button: {
      baseStyle: {
        borderRadius: "none",
      },
      variants: {
        solid: (props: StyleProps) => {
          if (props.colorScheme === "secondary") {
            return { color: "dark.500" };
          } else if (props.colorScheme === "dark") {
            return { bg: "dark.400", color: "bright.400" };
          }
        },
        outline: {
          border: "2px solid",
          color: "dark.500",
          borderColor: "dark.500",
          _active: {
            color: "primary.500",
            borderColor: "primary.500",
          },
          _hover: {
            color: "primary.500",
            borderColor: "primary.500",
          },
          _disabled: {
            color: "dark.500",
            pointerEvents: "none",
          },
        },
        link: {
          color: "dark.500",
          _active: {
            color: "primary.500",
          },
          _hover: {
            color: "primary.500",
            textDecoration: "none",
          },
          _disabled: {
            color: "dark.500",
            pointerEvents: "none",
          },
        },
      },
      sizes: {
        xs: {
          px: 3,
          height: 8,
          minW: 8,
        },
        sm: {
          px: 4,
          height: 10,
          minW: 10,
        },
        md: {
          px: 8,
          height: 12,
          minW: 12,
        },
        lg: {
          h: 14,
          px: 6,
          minW: 12,
          fontSize: "md",
        },
      },
      defaultProps: {
        colorScheme: "primary",
      },
    },
    CloseButton: {
      baseStyle: {
        mt: "1",
        borderRadius: "none",
        _hover: {
          bg: "dark.600",
        },
      },
    },
    Input: {
      sizes: {
        sm: {
          field: {
            borderRadius: "none",
          },
        },
        md: {
          field: {
            borderRadius: "none",
          },
        },
        lg: {
          field: {
            px: 4,
            borderRadius: "none",
          },
        },
      },
      variants: {
        outline: {
          field: {
            borderColor: "dark.400",
            border: "2px solid",
            _hover: {
              borderColor: "dark.400",
            },
            _focus: {
              borderColor: "secondary.500",
              boxShadow: "none",
            },
          },
        },
      },
      defaultProps: {
        focusBorderColor: "secondary.500",
        errorBorderColor: "primary.500",
      },
    },
    InputRightAddon: {
      baseStyle: {
        bg: "transparent",
      },
    },
    Link: {
      baseStyle: {
        color: "dark",
        _hover: {
          color: "primary.500",
        },
      },
    },
    Modal: {
      baseStyle: {
        dialog: {
          borderRadius: "none",
          boxShadow: "none",
          bg: "dark.500",
          color: "bright.400",
          mx: 2,
        },
        body: {
          py: 4,
        },
      },
      defaultProps: {
        isCentered: true,
      },
    },
    Divider: {
      baseStyle: (props: StyleProps) => ({
        borderColor: props.colorScheme === "bright" ? "bright.500" : "dark.500",
        opacity: props.colorScheme === "bright" ? 0.2 : 0.1,
        borderBottomWidth: "2px",
      }),
    },
    Tooltip: {
      baseStyle: {
        bg: "dark.400",
        color: "bright.400",
      },
    },
  },
};

export default theme;
